// OAuth flow for Capacitor (iOS/Android) via a platform-appropriate
// authentication session, both implemented as small native plugins in
// this app rather than via @capacitor/browser (which we evaluated and
// dropped — see the per-platform comments below).
//
// Android: native CustomTabs plugin (see CustomTabsPlugin.java) — raw
// VIEW intent + Custom Tabs extras, bypassing @capacitor/browser's
// BrowserControllerActivity wrapper whose onResume lifecycle mis-handles
// HTTPS App Link deep-link returns. Callback returns via appUrlOpen.
//
// iOS: native AuthSession plugin (see AuthSessionPlugin.swift) wrapping
// ASWebAuthenticationSession with the iOS 17.4+ .https callback variant.
// SFSafariViewController (what @capacitor/browser uses) doesn't trigger
// Universal Links app-handoff when the provider redirects the tab to
// the callback URL (UL only fires on external launches, not in-tab
// navigations). ASWebAuthenticationSession is Apple's purpose-built
// OAuth API that dismisses itself and returns the callback URL directly
// to the caller.

import { Capacitor, registerPlugin } from '@capacitor/core';

const platform = Capacitor.getPlatform();
const CustomTabs = platform === 'android' ? registerPlugin('CustomTabs') : null;
const AuthSession = platform === 'ios' ? registerPlugin('AuthSession') : null;

/** @type {Promise<{code: string, state: string}> | null} */
let _inFlightLogin = null;
/** @type {Promise<void> | null} */
let _inFlightLogout = null;

/**
 * Open the OAuth authorize URL in a system browser and return the
 * authorization code when the callback URL triggers appUrlOpen.
 * @param {string} authorizeUrl - The full OAuth authorize URL
 * @param {string} redirectUri - The redirect URI to watch for
 * @returns {Promise<{code: string, state: string}>}
 */
export async function openInAppBrowserLogin(authorizeUrl, redirectUri) {
  if (_inFlightLogin) return _inFlightLogin;
  _inFlightLogin = (async () => {
    try {
      return await _runFlow(authorizeUrl, redirectUri, /*isLogout*/ false);
    } finally {
      _inFlightLogin = null;
    }
  })();
  return _inFlightLogin;
}

/**
 * Open the d3-auth logout URL in the system browser to clear the session.
 * Resolves when the returnTo URL triggers appUrlOpen.
 * @param {string} logoutUrl
 * @param {string} returnTo
 */
export async function openInAppBrowserLogout(logoutUrl, returnTo) {
  if (_inFlightLogout) return _inFlightLogout;
  _inFlightLogout = (async () => {
    try {
      await _runFlow(logoutUrl, returnTo, /*isLogout*/ true);
    } finally {
      _inFlightLogout = null;
    }
  })();
  return _inFlightLogout;
}

/**
 * Launch `url` in an OAuth authentication session. Resolves with
 * `{code, state}` on login flows; resolves `undefined` on logout flows.
 * @param {string} url
 * @param {string} matchUri
 * @param {boolean} isLogout
 */
async function _runFlow(url, matchUri, isLogout) {
  if (AuthSession) {
    // iOS: ASWebAuthenticationSession delivers the full callback URL as
    // its result. No appUrlOpen listener needed.
    try {
      const { url: callbackUrl } = await AuthSession.start({ url, callbackUrl: matchUri });
      if (isLogout) return;
      const parsed = new URL(callbackUrl);
      const code = parsed.searchParams.get('code');
      const state = parsed.searchParams.get('state');
      if (!code) throw new Error('No authorization code in callback');
      return { code, state: state || '' };
    } catch (err) {
      if (err?.message === 'cancelled') {
        if (isLogout) return;
        throw new Error('Login cancelled', { cause: err });
      }
      throw err;
    }
  }

  // Android: launch Chrome Custom Tab, wait for App Link deep-link return.
  const { App } = await import('@capacitor/app');

  return new Promise((resolve, reject) => {
    let settled = false;
    /** @type {import('@capacitor/core').PluginListenerHandle} */
    let urlHandle;
    /** @type {import('@capacitor/core').PluginListenerHandle} */
    let stateHandle;
    let wentInactive = false;

    const cleanup = async () => {
      await urlHandle?.remove();
      await stateHandle?.remove();
    };

    const settle = (fn) => {
      if (settled) return;
      settled = true;
      cleanup().then(fn);
    };

    Promise.all([
      App.addListener('appUrlOpen', (event) => {
        const u = event.url || '';
        if (!u.startsWith(matchUri)) return;
        settle(() => {
          if (isLogout) return resolve();
          try {
            const parsed = new URL(u);
            const code = parsed.searchParams.get('code');
            const state = parsed.searchParams.get('state');
            if (code) resolve({ code, state: state || '' });
            else reject(new Error('No authorization code in callback'));
          } catch (err) {
            reject(err);
          }
        });
      }).then((h) => {
        urlHandle = h;
      }),
      // Detect cancellation: if the app foregrounds again without our
      // expected appUrlOpen having fired, the user dismissed the browser.
      App.addListener('appStateChange', (state) => {
        if (!state.isActive) {
          wentInactive = true;
          return;
        }
        if (!wentInactive || settled) return;
        // give appUrlOpen a brief window to fire if it's racing with us
        setTimeout(() => {
          settle(() => (isLogout ? resolve() : reject(new Error('Login cancelled'))));
        }, 300);
      }).then((h) => {
        stateHandle = h;
      }),
    ]).then(() => {
      CustomTabs.open({ url });
    });
  });
}
